import { cn } from '@exam-prep/ui';
import { useMemo } from 'react';
import {
  EXAM,
  useAwaySignal,
  useExamProtection,
  watermarkBackground,
  watermarkLabel,
} from '../exam-guard';
import { ErrorBox, Loading } from '../../components/Feedback';
import { usePlayerActions } from './hooks/usePlayerActions';
import { usePlayerClock } from './hooks/usePlayerClock';
import { usePlayerSession } from './hooks/usePlayerSession';
import { useReportQuestion } from './hooks/useReportQuestion';
import { answerCounts, isLastUnit, openQuestions } from './model/palette';
import { PalettePanel } from './ui/PalettePanel';
import { ExamWatermark, PlayerHeader, ProtectionNotice, StatusBars } from './ui/PlayerChrome';
import { PlayerFooter } from './ui/PlayerFooter';
import { QuestionPane } from './ui/QuestionPane';

interface PlayerProps {
  attemptId: string;
  /** Telegram user id for the watermark (SSC Rankers League only). */
  userId?: number | null;
  /** `managed` is true for an SSC Rankers League attempt (it has its own results screen). */
  onDone: (managed: boolean) => void;
}

export function Player({ attemptId, userId = null, onDone }: PlayerProps) {
  const s = usePlayerSession(attemptId, onDone);
  const report = useReportQuestion(attemptId);
  const { view, managed } = s;
  // SSC Rankers League only: copy deterrents and leave-screen reports (no effect on normal mocks).
  const blockedNotice = useExamProtection(managed);
  const returned = useAwaySignal(attemptId, managed);
  const { remaining, expired } = usePlayerClock({
    attemptId,
    view,
    now: s.now,
    offset: s.offset,
    managed,
    queue: s.queue,
    load: s.load,
  });
  const actions = usePlayerActions({
    attemptId,
    queue: s.queue,
    timer: s.timer,
    managed,
    load: s.load,
    onDone,
    setNotice: s.setNotice,
    setError: s.setError,
    setLocal: s.setLocal,
  });

  const questions = useMemo(() => openQuestions(view), [view]);
  const q = questions[Math.min(s.idx, Math.max(0, questions.length - 1))];
  const cur = q ? (s.local[q.itemId] ?? { choice: q.choice, marked: q.marked }) : null;

  if (s.error) return <ErrorBox error={s.error} retry={() => void s.load()} />;
  if (!view) return <Loading />;
  const locked = !!view.paused || expired || actions.transitioning;
  const set = (patch: Partial<NonNullable<typeof cur>>) => {
    if (!q || !cur || locked) return;
    actions.answer(q, cur, patch);
  };

  return (
    <div className={cn('flex min-h-screen flex-col', managed && 'exam-protected')}>
      {managed ? (
        <ExamWatermark
          background={watermarkBackground(watermarkLabel(userId, view.watermark ?? null))}
        />
      ) : null}
      {managed && (blockedNotice || returned) ? (
        <ProtectionNotice text={blockedNotice ?? EXAM.away} />
      ) : null}
      <PlayerHeader title={view.mock.title} remaining={remaining} />
      <StatusBars
        offline={s.offline}
        waiting={s.waiting}
        managed={managed}
        paused={!!view.paused}
        expired={expired}
        notice={s.notice}
        onDismissNotice={() => s.setNotice(null)}
      />

      <main className="flex-1 space-y-4 p-3">
        {q && cur ? (
          <QuestionPane
            report={report}
            question={q}
            current={cur}
            ui={view.paper.ui}
            locked={locked}
            transitioning={actions.transitioning}
            isFirst={s.idx === 0}
            isLast={s.idx >= questions.length - 1}
            onChoose={(i) => set({ choice: i })}
            onMark={() => set({ marked: !cur.marked })}
            onClear={() => set({ choice: null })}
            onPrev={() => s.visit(view, s.idx - 1)}
            onNext={() => s.visit(view, s.idx + 1)}
          />
        ) : null}
        <PalettePanel
          sections={view.sections}
          local={s.local}
          questions={questions}
          currentId={q?.itemId}
          disabled={actions.transitioning}
          onVisit={(at) => s.visit(view, at)}
        />
      </main>

      <PlayerFooter
        confirm={actions.confirm}
        counts={answerCounts(view, s.local)}
        managed={managed}
        lastUnit={isLastUnit(view)}
        transitioning={actions.transitioning}
        onAsk={actions.setConfirm}
        onCancel={() => actions.setConfirm(null)}
        onConfirm={(action) => void actions.finish(action)}
      />
    </div>
  );
}
